import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link, useNavigate } from "react-router-dom";
import NoticeTable from "./NoticeTable";
import NoticeDrawer from "./NoticeDrawer";
import { INotice } from "../../exports/interfaces";
import { formatDate } from "../../exports/functions";
import { usePermission } from "../../hooks/usePermission";
import { FilePlus2 } from "lucide-react";
import { useOption } from "../../context/OptionContext";

const Notice = ({
  notice,
  setNotice,
}: {
  notice: INotice[];
  setNotice: Dispatch<SetStateAction<INotice[]>>;
}) => {
  const isMobile = useMobile();
  const { categories } = useOption();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const navigate = useNavigate();
  const { canCreate } = usePermission();
  const now = new Date();

  const noticesThisMonth = notice.filter((n) => {
    const date = new Date(n.publishDate);

    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  }).length;
  const lastUpdated = notice.length
    ? new Date(Math.max(...notice.map((n) => new Date(n.createdAt).getTime())))
    : null;
  const noticeTypes = categories.find((item) => item.name === "Notice Type");
  const noticeTypeCounts =
    noticeTypes?.dropdowns.map((dropdown) => ({
      label: dropdown.label,
      count: notice.filter((item) => item.noticeType === dropdown.label).length,
    })) ?? [];

  const activeNoticeCount = notice.filter((n) => n.status === "Active").length;
  return (
    <>
      <div
        id="notice-header"
        className="d-flex justify-content-between align-items-center mb-3"
      >
        <div>
          <h3>Notices List</h3>
          <small>
            {noticeTypeCounts
              .slice(0, 3)
              .map((item) => `${item.count} ${item.label.toUpperCase()}`)
              .join(", ")}
          </small>
        </div>

        {canCreate("notices") && (
          <button
            className="btn px-3 btn-badge"
            onClick={() => navigate("/notices/create")}
          >
            <i className={`${!isMobile ? "me-2" : ""}`}>
              <FilePlus2 size={20} />
            </i>
            {!isMobile && "Create New Notice"}
          </button>
        )}
      </div>
      <div className="row g-2">
        <div className="col-md-3">
          <div
            id="notice-posted-month"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p>Notice Posted This Month</p>
              <h2>{noticesThisMonth}</h2>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap mt-3">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
              <Link to="/">
                <small>View all</small>
              </Link>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div
            id="notice-types"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p className="mb-1">Types of Notice</p>
              <div className="row g-3 mb-2">
                {noticeTypeCounts.slice(0, 4).map((item) => (
                  <div key={item.label} className="col-md-3 col-6">
                    <p>{item.label}</p>
                    <h2 className="my-0">{item.count}</h2>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-start">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div
            id="notice-archive"
            className="dashboard-card h-100 d-flex flex-column"
          >
            <div className="card-body">
              <p>Notice Archive</p>
              <h2>{activeNoticeCount}</h2>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap mt-3">
              <small>Last Updated: {formatDate(lastUpdated)}</small>
              <Link to="/">
                <small>View all</small>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div id="notice-table" className="row g-2 dashboard-section">
        <div className="col-md-12">
          <NoticeTable notice={notice} setNotice={setNotice} />
        </div>
      </div>
      <NoticeDrawer
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        setNotice={setNotice}
      />
    </>
  );
};

export default Notice;
